import { Box, Chip, Paper, Stack } from '@mui/material'
import React from 'react'

import GlyphIcon from 'components/common/GlyphIcon'
import GlyphSubIcon from 'components/common/GlyphSubIcon'
import { TruncatedTextWithTooltip } from 'components/common/TruncatedTextWithTooltip'
import Icon from 'components/Icon'
import {
  GRAPH_NODE_PAPER_PROPS,
  NODE_TEXT_MAX_WIDTH
} from 'components/scenarios/concurrent/concurrent-scenario-form-utils'
import {
  getLogo,
  getPresetKeyFromURL
} from 'components/scenarios/create/status-checks/third-party-presets'
import { getStatusCheckDetails } from 'components/scenarios/utils'
import { getStateOfNodeByType } from 'components/scenarios/utils/graph-node-lifecycle'
import {
  isCustomStatusCheck,
  StatusCheckCategoryLabel
} from 'components/status-checks/status-checks-form-utils'
import { trans } from 'i18n/translate'
import { muiTheme } from 'mui-theme'
import { useGetStatusChecks } from 'queries/status-checks/use-get-status-checks'
import theme from 'theme'
import {
  type ContinuousStatusCheckNode,
  type SynchronousStatusCheckNode
} from 'types/api/scenarios/scenario'
import { StatusCheckCategory } from 'types/api/status-checks/status-check'

export const CATEGORY_FALLBACK_NAME = 'Unknown category'

/* ((Number of chars) * (estimated width of char)) + (gap size) */
const CATEGORY_CHIP_MAX_WIDTH = CATEGORY_FALLBACK_NAME.length * 8.25 + 28

const ICON_STYLES = {
  height: muiTheme.spacing(4),
  width: muiTheme.spacing(4)
}

/**
 * Gets a status check category display name for display in the chip.
 */
function getCategoryDisplayName(
  node: SynchronousStatusCheckNode | ContinuousStatusCheckNode
) {
  const { isAutoGenerated, category, name } = node

  if (category) {
    return category === StatusCheckCategory.UNKNOWN
      ? CATEGORY_FALLBACK_NAME
      : StatusCheckCategoryLabel[category]
  }

  if (isAutoGenerated && !category) {
    const nameLowered = name.toLowerCase()

    if (nameLowered.includes('error')) {
      return StatusCheckCategoryLabel.ERRORS
    }
    if (nameLowered.includes('latency')) {
      return StatusCheckCategoryLabel.LATENCY
    }
    if (nameLowered.includes('request')) {
      return StatusCheckCategoryLabel.REQUESTS
    }
  }

  return CATEGORY_FALLBACK_NAME
}

/**
 * If the endpoint url is one of the known services, then replace the hostname with the service name
 * Otherwise return the hostname portion of the url
 */
function parseDisplayUrl(url?: string) {
  return url && url.replace('https://', '')
}

export const StatusCheckGraphNode: React.FC<{
  node: SynchronousStatusCheckNode | ContinuousStatusCheckNode
  index?: number
  overview?: boolean
  rowActions?: React.ReactNode
  isClickable?: boolean
  onClick?: (
    node: SynchronousStatusCheckNode | ContinuousStatusCheckNode
  ) => void
}> = ({
  node,
  index = 1,
  rowActions = null,
  isClickable = false,
  onClick = () => null
}) => {
  const { isNotStarted, isActive } = getStateOfNodeByType(node)
  const { data: statusChecksRaw } = useGetStatusChecks({
    refetchOnMount: false
  })

  const endpointUrl = node.endpointConfiguration?.url
  const displayUrl = parseDisplayUrl(endpointUrl)

  const nodeStatusCheck = statusChecksRaw?.find(
    (statusCheck) => statusCheck.identifier === node?.statusCheckId
  )

  const isCustom = node.isAutoGenerated
    ? false
    : isCustomStatusCheck(nodeStatusCheck)

  const { color, icon } = getStatusCheckDetails(node)

  const category = getCategoryDisplayName(node)

  const nodeId = node.guid ?? node.id ?? node.autoGeneratedStatusCheckId

  return (
    <Paper
      data-testid={`status-check-graph-node-${nodeId}`}
      sx={[
        GRAPH_NODE_PAPER_PROPS,
        !rowActions && { paddingRight: 2 },
        (muiTheme) => ({
          marginY: muiTheme.spacing(2)
        })
      ]}
      {...(isClickable && { onClick: () => onClick(node) })}
    >
      <Stack
        aria-label={`${trans('status_checks.label.singular')} ${index + 1}`}
        direction="row"
        sx={{
          justifyContent: 'space-between'
        }}
      >
        <Stack direction="row" sx={{ alignItems: 'center', columnGap: 1.5 }}>
          <GlyphIcon
            backgroundColor={isCustom ? theme.colors.steel[2] : 'none'}
            css={ICON_STYLES}
          >
            {node.isAutoGenerated ? (
              <Icon css={ICON_STYLES} icon="statusCheckGremlinIcon" />
            ) : isCustom ? (
              <Icon
                css={{
                  ...ICON_STYLES,
                  color: theme.colors.gray[1]
                }}
                icon="wavePulse"
              />
            ) : (
              <img
                css={ICON_STYLES}
                src={getLogo(
                  getPresetKeyFromURL(node?.endpointConfiguration?.url)
                )}
              />
            )}
            {!isNotStarted && !isActive && (
              <GlyphSubIcon
                css={{
                  background: color,
                  height: muiTheme.spacing(2),
                  width: muiTheme.spacing(2),
                  bottom: 0,
                  right: `-${muiTheme.spacing(1)}`,
                  border: `2px solid ${muiTheme.palette.common.white}`,
                  padding: 0
                }}
              >
                <Icon
                  css={{
                    margin: 'auto'
                  }}
                  icon={icon}
                />
              </GlyphSubIcon>
            )}
          </GlyphIcon>
          <Stack>
            <Stack
              direction="row"
              sx={{ alignItems: 'center', columnGap: 1, flexWrap: 'wrap' }}
            >
              <TruncatedTextWithTooltip
                data-test-status-check-name
                aria-label={`${trans('status_checks.label.singular')} ${
                  node.name
                }`}
                sx={{
                  fontSize: muiTheme.typography.body2.fontSize,
                  fontWeight: muiTheme.typography.fontWeightBold,
                  maxWidth: `calc(${NODE_TEXT_MAX_WIDTH}px - ${
                    category ? CATEGORY_CHIP_MAX_WIDTH : 0
                  }px)`
                }}
                tooltipProps={{ title: node.name }}
                variant="h4"
              >
                {node.name}
              </TruncatedTextWithTooltip>
              {category && <Chip label={category} role="status" size="small" />}
            </Stack>
            {!isClickable && (
              <TruncatedTextWithTooltip
                data-test-status-check-url
                aria-label={`URL: ${endpointUrl}`}
                sx={{ maxWidth: NODE_TEXT_MAX_WIDTH }}
                tooltipProps={{ title: endpointUrl }}
                variant="caption"
              >
                {displayUrl}
              </TruncatedTextWithTooltip>
            )}
          </Stack>
        </Stack>

        {rowActions && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignContent: 'center'
            }}
          >
            {rowActions}
          </Box>
        )}
      </Stack>
    </Paper>
  )
}
